import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  cn,
  currentFyStartYear,
  formatFyLabel,
  fyKey,
  parseFyStartYear,
} from "../../../lib/utils";

interface FinancialYearPickerProps {
  value: string;
  onChange: (fy: string) => void;
}

const WINDOW = 12;

const FinancialYearPicker = ({ value, onChange }: FinancialYearPickerProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentStart = currentFyStartYear();
  const selectedStart = useMemo(
    () => parseFyStartYear(value) ?? currentStart,
    [value, currentStart]
  );
  const [windowEnd, setWindowEnd] = useState(selectedStart);

  useEffect(() => setWindowEnd(selectedStart), [selectedStart]);

  const canGoLater = windowEnd < currentStart;

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const cappedEnd = Math.min(windowEnd, currentStart);
  const years = Array.from({ length: WINDOW }, (_, i) => cappedEnd - (WINDOW - 1) + i);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-9 items-center justify-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-medium text-[#171717] whitespace-nowrap shadow-sm transition-opacity hover:opacity-80"
      >
        <CalendarDays size={16} strokeWidth={2} />
        {formatFyLabel(value)}
        <ChevronDown size={16} strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 w-64 rounded-md border border-[#E5E5E5] bg-white p-3 shadow-md">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Earlier years"
              onClick={() => setWindowEnd((y) => y - WINDOW)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#525252] hover:bg-[#F5F5F4]"
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
            <span className="text-sm font-semibold text-[#0A0A0A]">
              {formatFyLabel(fyKey(years[0]))} –{" "}
              {formatFyLabel(fyKey(years[years.length - 1]))}
            </span>
            <button
              type="button"
              aria-label="Later years"
              disabled={!canGoLater}
              onClick={() => canGoLater && setWindowEnd((y) => Math.min(y + WINDOW, currentStart))}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#525252] hover:bg-[#F5F5F4] disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {years.map((startYear) => {
              const isSelected = startYear === selectedStart;
              return (
                <button
                  key={startYear}
                  type="button"
                  onClick={() => {
                    onChange(fyKey(startYear));
                    setOpen(false);
                  }}
                  className={cn(
                    "rounded-md py-2 text-xs font-medium transition-colors",
                    isSelected
                      ? "bg-[#3551F3] text-white"
                      : "text-[#374151] hover:bg-[#F5F5F4]"
                  )}
                >
                  {formatFyLabel(fyKey(startYear))}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialYearPicker;
